import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAccessToken } from '../Auth/AuthStore'
import axios from 'axios'
import Header from '../Layout/Header'
import StatsCard from './StatsCard'
import RecommendationCard from './RecommendationCard'
import EmotionChart from './EmotionChart'
import ActivitySidebar from './ActivitySidebar'
import HealingCourseProgress from './HealingCourseProgress'
import styles from './Dashboard.module.css'
import helloicon from '../../assets/icons/hello.svg'
import aiIcon from '../../assets/icons/ai.svg'
import calendaricon from '../../assets/icons/calendar.svg'
import communityicon from '../../assets/icons/community2.svg'
import pointicon from '../../assets/icons/point.svg'
import paletteicon from '../../assets/icons/palette.svg'
import medalicon from '../../assets/icons/medal.svg'
import golficon from '../../assets/icons/golf.svg'
import peopleicon from '../../assets/icons/people.svg'



const Dashboard = () => {
  const navigate = useNavigate()
  const [username, setUsername] = useState('') 
  const [programProgress, setProgramProgress] = useState(null)
  const [dashboardStats, setDashboardStats] = useState({
    drawingCount: 0,
    articleCount: 0,
    completedProgramCount: 0
  })

  const healingQuotes = [
    "잠깐 쉬어도 괜찮아. 너무 잘하고 있어요.",
    "오늘 하루도 고생 많았어.",
    "작은 한 걸음도 큰 변화의 시작이야.",
    "지금 이 순간에 집중해볼까?",
    "너무 잘하려고 하지 않아도 돼."
  ]

  const drawingTopics = [
    "5분 동안 조용히 색칠하기",
    "오늘 느낀 감정 3개 적어보기",
    "스케치 1개만 완성하기",
    "30초 동안 심호흡하기",
    "나에게 긍정 문장 1개 적기"
  ]

  const [randomHealingQuote] = useState(() => 
    healingQuotes[Math.floor(Math.random() * healingQuotes.length)]
  )
  const [randomDrawingTopic] = useState(() => 
    drawingTopics[Math.floor(Math.random() * drawingTopics.length)]
  )

  const statsData = useMemo(() => [
    { 
      title: '총 작품 수', 
      value: String(dashboardStats.drawingCount), 
      color: '#DBEAFE',
      icon: <img src={paletteicon} alt="palette" />,
      onClick: () => navigate('/gallery')
    },
    { 
      title: '게시글 수', 
      value: String(dashboardStats.articleCount), 
      color: '#F3E8FF',
      icon: <img src={communityicon} alt="community" className={styles.communityicon} />,
      onClick: () => navigate('/community', { state: { tab: 'myPosts' } })
    },
    { 
      title: '코스 완주 횟수', 
      value: String(dashboardStats.completedProgramCount), 
      color: '#DCFCE7',
      icon: <img src={medalicon} alt="medal" />
    }
  ], [dashboardStats, navigate])

  const recommendationCards = useMemo(() => {
    const progressLabel = programProgress
      ? `${programProgress.completedCourseCount}/${programProgress.totalDays}일`
      : '참여 전'
    return [
      {
        title: '오늘의 힐링 문구',
        subtitle: `${username}님을 위한 한마디`,
        description: randomHealingQuote,
        content: '주변 친구에게도 말해주세요!',
        color: 'linear-gradient(270deg,#BFDBFE 0%,#DBEAFE 100%)',
        textColor: '#0029CD',
        icon: <img src={aiIcon} alt="AI" />
      },
      {
        title: '오늘의 미션',
        subtitle: '목표를 향한 한 걸음',
        description: randomDrawingTopic,
        content: '보상: 뿌듯함 10점', 
        color: 'linear-gradient(270deg,#A7F3D0 0%,#D1FAE5 100%)',
        textColor: '#00510F',
        icon: <img src={golficon} alt="golf" />
      },
      {
        title: '진행 중인 프로그램',
        subtitle: '꾸준한 성취를 이어가요',
        description: '스트레스 관리 5일 코스',
        progress: progressLabel,
        color: 'linear-gradient(270deg,#DDD6FE 0%,#EDE9FE 100%)',
        textColor: '#7100B3',
        icon: <img src={calendaricon} alt="calendar" />
      },
      {
        title: '커뮤니티 참여',
        subtitle: '사람들과 그림으로 연결되는 순간',
        description: '인기 게시글의 주인공이 되어보세요',
        content: '지금 바로 도전!',
        color: 'linear-gradient(270deg,#FFDCB9 0%,#FFF1B8 100%)',
        textColor: '#926400',
        icon: <img src={peopleicon} alt="people" />
      }
    ]
  }, [programProgress, username, randomHealingQuote, randomDrawingTopic])

  useEffect(() => {
    const fetchUserData = async () => {
      try {

        const token = getAccessToken();
        if (!token) throw new Error('JWT 발급 실패: token 없음');

        const response = await axios.get(`${import.meta.env.VITE_API_BASE}/api/user/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          }, 
        })

        setUsername(response.data.data.username)
      } catch (error) {
        console.error('유저 정보를 불러오는데 실패했습니다:', error)
      }
    }

    fetchUserData()
  }, [])

  const fetchProgramProgress = useCallback(async () => {
    try {
      const token = getAccessToken()
      if (!token){
        console.error('토큰이 없습니다.')
        return
      }
      
      const response = await axios.get(`${import.meta.env.VITE_API_BASE}/api/programs/ongoing`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      console.log("프로그램 진행 정보:", response.data?.data)
      setProgramProgress(response.data?.data || null)
    } catch (error) {
      console.error('프로그램 진행 정보를 불러오는데 실패했습니다:', error)
    }
  }, [])

  useEffect(() => {
    fetchProgramProgress()
  }, [fetchProgramProgress])

  const fetchDashboardStats = useCallback(async () => {
    try {
      const token = getAccessToken()
      if (!token) {
        console.error('토큰이 없습니다.')
        return
      }

      const response = await axios.get(`${import.meta.env.VITE_API_BASE}/api/user/dashboard/stats`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data?.data) {
        setDashboardStats({
          drawingCount: response.data.data.drawingCount || 0,
          articleCount: response.data.data.articleCount || 0,
          completedProgramCount: response.data.data.completedProgramCount || 0
        })
      }
    } catch (error) {
      console.error('대시보드 통계 정보를 불러오는데 실패했습니다:', error)
    }
  }, [])

  useEffect(() => {
    fetchDashboardStats()
  }, [fetchDashboardStats])

  return (
    <div className={styles.dashboard}>
      <Header />
      
      <div className={styles.mainContent}>
        <div className={styles.leftColumn}>
          {/* 환영 메시지 */}
          <div className={styles.welcomeCard}>
            <h2>반가워요, {username}님!</h2>
            <p>오늘 하루는 어떠나요?</p>
            <p>우리 같이 오늘하루를 그림으로 그려 보아요!</p>
            <div className={styles.helloicon}>
              <img src={helloicon} alt="hello" />
            </div>
          </div>

          {/* 통계 카드들 */}
          <div className={styles.statsGrid}>
            {statsData.map((stat, index) => (
              <StatsCard 
                key={index}
                title={stat.title}
                value={stat.value}
                color={stat.color}
                icon={stat.icon}
                onClick={stat.onClick}  
              />
            ))}
          </div>

          {/* 오늘의 추천 섹션 */}
          <section className={styles.recommendationSection}>
            <h3></h3>
            <div className={styles.recommendationGrid}>
              {recommendationCards.map((card, index) => (
                <RecommendationCard 
                  key={index}
                  {...card}
                />
              ))}
            </div>
          </section>

          {/* 치유 코스 진행도 */}
          <section className={styles.chartSection}>
            <h3>치유 코스 진행도</h3>
            <HealingCourseProgress 
              progressData={programProgress}
              onEnrollSuccess={fetchProgramProgress}
              isParticipated={programProgress?.isParticipated ?? false}
            />
          </section>
          

          {/* 감정 변화 차트 */}
          {/*<section className={styles.chartSection}>
            <h3>감정 변화 추이</h3>
            <EmotionChart />
          </section>*/}
        </div>

        <div className={styles.rightColumn}>
          <ActivitySidebar />
        </div>
      </div>
    </div>
  )
}

export default Dashboard